/**
 * F-148 §3.3 scoped idempotency store.
 *
 * - Post key: generated when Review first loads for `(draftId, version)` and kept in
 *   `sessionStorage` under `f148:post:<draftId>:<version>` so a retry after a timeout or a
 *   reload reuses the same key (AC16).
 * - Correction key: generated when the correction dialog opens; held in component state only.
 * - Save token: a UUID per save attempt, reused only for retries of that same payload (§2.3).
 *
 * The storage is injectable so tests never touch the real `sessionStorage`. When storage is
 * unavailable (private mode, quota, SSR) the store falls back to an in-memory map.
 */

export const POST_KEY_PREFIX = 'f148:post:';

export interface IdempotencyStorage {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
  /** Keys currently held; used to clear every version of a draft. */
  keys: () => string[];
}

export interface IdempotencyStore {
  /** Returns the stored post key for `(draftId, version)`, creating it on first access. */
  getOrCreatePostKey: (draftId: string, version: number) => string;
  /** Read-only peek: null when no key exists yet. */
  peekPostKey: (draftId: string, version: number) => string | null;
  /** Removes every post key for the draft (after a successful post or a discard). */
  clearPostKeys: (draftId: string) => void;
}

export function postKeyStorageKey(draftId: string, version: number): string {
  return `${POST_KEY_PREFIX}${draftId}:${version}`;
}

/** Formats 16 bytes as an RFC 4122 v4 UUID string, forcing the version (4) and variant (10xx) bits. */
function formatUuidV4(bytes: Uint8Array): string {
  const octets = Array.from({ length: 16 }, (_, i) => bytes[i] ?? 0);
  octets[6] = ((octets[6] ?? 0) & 0x0f) | 0x40;
  octets[8] = ((octets[8] ?? 0) & 0x3f) | 0x80;
  const hex = octets.map((b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`;
}

/**
 * Idempotency/deduplication key: always a syntactically valid UUID v4 (the server column is
 * `uuid`). Prefers `crypto.randomUUID`, then `crypto.getRandomValues`; the last resort is a
 * `Math.random`-filled 16-byte buffer, which is fixed-length and valid for every byte value
 * (including `Math.random() === 0`). These keys deduplicate retries only — they are NOT
 * authorization or security tokens, so the final fallback's weaker randomness is acceptable.
 */
export function createIdempotencyKey(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const bytes = new Uint8Array(16);
  if (c && typeof c.getRandomValues === 'function') {
    c.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  return formatUuidV4(bytes);
}

/** Alias kept for readability at call sites: every save attempt gets a fresh token. */
export const createSaveToken = createIdempotencyKey;

function memoryStorage(): IdempotencyStorage {
  const map = new Map<string, string>();
  return {
    getItem: (key) => map.get(key) ?? null,
    setItem: (key, value) => {
      map.set(key, value);
    },
    removeItem: (key) => {
      map.delete(key);
    },
    keys: () => Array.from(map.keys()),
  };
}

function webStorage(storage: Storage): IdempotencyStorage {
  return {
    getItem: (key) => storage.getItem(key),
    setItem: (key, value) => storage.setItem(key, value),
    removeItem: (key) => storage.removeItem(key),
    keys: () => {
      const out: string[] = [];
      for (let i = 0; i < storage.length; i += 1) {
        const key = storage.key(i);
        if (key !== null) out.push(key);
      }
      return out;
    },
  };
}

function resolveDefaultStorage(): IdempotencyStorage {
  try {
    const storage = globalThis.sessionStorage;
    if (!storage) return memoryStorage();
    const probe = `${POST_KEY_PREFIX}__probe__`;
    storage.setItem(probe, '1');
    storage.removeItem(probe);
    return webStorage(storage);
  } catch {
    return memoryStorage();
  }
}

export function createIdempotencyStore(storage?: IdempotencyStorage): IdempotencyStore {
  const backing = storage ?? resolveDefaultStorage();
  const fallback = memoryStorage();

  const read = (key: string): string | null => {
    try {
      return backing.getItem(key) ?? fallback.getItem(key);
    } catch {
      return fallback.getItem(key);
    }
  };
  const write = (key: string, value: string): void => {
    try {
      backing.setItem(key, value);
    } catch {
      fallback.setItem(key, value);
    }
  };
  const remove = (key: string): void => {
    try {
      backing.removeItem(key);
    } catch {
      // ignore: the fallback removal below still runs
    }
    fallback.removeItem(key);
  };
  const allKeys = (): string[] => {
    let keys: string[] = [];
    try {
      keys = backing.keys();
    } catch {
      keys = [];
    }
    return Array.from(new Set([...keys, ...fallback.keys()]));
  };

  return {
    getOrCreatePostKey: (draftId, version) => {
      const key = postKeyStorageKey(draftId, version);
      const existing = read(key);
      if (existing) return existing;
      const created = createIdempotencyKey();
      write(key, created);
      return created;
    },
    peekPostKey: (draftId, version) => read(postKeyStorageKey(draftId, version)),
    clearPostKeys: (draftId) => {
      const prefix = `${POST_KEY_PREFIX}${draftId}:`;
      for (const key of allKeys()) {
        if (key.startsWith(prefix)) remove(key);
      }
    },
  };
}

/** Module-level default store used by routes; tests build their own with `createIdempotencyStore`. */
export const idempotencyStore: IdempotencyStore = createIdempotencyStore();
